<script setup lang="ts">
  import { Tooltip as ArkTooltip } from '@ark-ui/vue';
  import { computed, onMounted } from 'vue';
  import { GLOBAL_OVERLAYS_TARGET } from '../config/global-overlays-target';
  import {
    arrowClass,
    arrowTipClass,
    contentClass,
    contentSizeClasses,
    descriptionClass,
    positionerClass,
    titleClass,
  } from './Tooltip.css';
  import {
    TooltipPosition,
    TooltipSize,
    type TooltipProps,
  } from './Tooltip.types';

  const props = withDefaults(defineProps<TooltipProps>(), {
    position: TooltipPosition.TOP,
    openDelay: 100,
    interactive: true,
    size: TooltipSize.Md,
  });

  const contentClasses = computed(() => [
    contentClass,
    contentSizeClasses[props.size],
  ]);

  defineOptions({
    inheritAttrs: false,
  });

  const hasContent = computed(() => !!props.title || !!props.description);

  const rootProps = computed(() => ({
    id: props.id,
    interactive: props.interactive,
    openDelay: props.openDelay,
    positioning: {
      placement: props.position,
      ...(props.offset !== undefined ? { offset: props.offset } : {}),
    },
  }));

  const ensureOverlaysTarget = () => {
    if (!document.querySelector(`.${GLOBAL_OVERLAYS_TARGET}`)) {
      const el = document.createElement('div');
      el.classList.add(GLOBAL_OVERLAYS_TARGET);
      document.body.appendChild(el);
    }
  };

  onMounted(() => {
    if (hasContent.value) {
      ensureOverlaysTarget();
    }
  });
</script>

<template>
  <ArkTooltip.Root v-if="hasContent" v-bind="rootProps">
    <ArkTooltip.Trigger as-child :class="tooltipTriggerClass">
      <slot />
    </ArkTooltip.Trigger>

    <Teleport defer :to="`.${GLOBAL_OVERLAYS_TARGET}`">
      <ArkTooltip.Positioner :class="positionerClass">
        <ArkTooltip.Content :class="contentClasses" v-bind="$attrs">
          <span v-if="title" :class="titleClass">{{ title }}</span>
          <span v-if="description" :class="descriptionClass">
            {{ description }}
          </span>
          <ArkTooltip.Arrow :class="arrowClass">
            <ArkTooltip.ArrowTip
              :class="arrowTipClass"
              data-testid="tooltip-arrow"
            />
          </ArkTooltip.Arrow>
        </ArkTooltip.Content>
      </ArkTooltip.Positioner>
    </Teleport>
  </ArkTooltip.Root>
  <slot v-else />
</template>
